'use strict';

require('../scss/chat.scss');

const SV = require('server-vars');
const get = require('lodash.get');
const root = typeof window === 'undefined' ? global : window;
const {localStorage, sessionStorage} = require('dibs-browser-storage');
const printRegex = /^\/print\//;
const isPrintPDP = root.location && printRegex.test(root.location.pathname);
const qs = require('querystring');
const { getUserId } = require('dibs-cookie-jar');
const { graphql } = require('react-relay');
const ScriptInjector = require('./scriptInjector');

const isPageType = require('./isPageType');
const {PAGE_TYPE, BROWSE_TYPES} = require('./pageConstants');
const {tradePromoDate} = (root.location && qs.parse(root.location.search)) || {};

const TYPE_SEARCH = 'SEARCH';
const TYPE_DEALER = 'SELLER';
const CHAT_STARTED_STORAGE_KEY = '__chatStarted';
const ARGUMENTS_STORAGE_KEY = '__chatArguments';
const CHECKOUT_UNLOAD_KEY = '__checkoutUnload';
const CHECKOUT_TYPE_KEY = '__checkoutTransactionType';
const CUSTOMER_TYPE_VIP = 'VIP';
const CUSTOMER_TYPE_TRADE = 'Trade';
const CUSTOMER_TYPE_CONSUMER = 'Consumer';

const ITEMS_ABOVE_PRICE_VIEWED_KEY = 'itemsAbovePriceViewed';
const ITEMS_ABOVE_PRICE_VIEWED_THRESHOLD = 3;

const PRICE_LIMIT_10K = 10000;
const PRICE_LIMIT_50K = 50000;

const TRADE_PROMO_DATE_FROM = tradePromoDate || '2017-01-16';

const intercomId = SV.get('intercomId');

const PAGE_TYPE_WHITELIST = [
    BROWSE_TYPES,
    PAGE_TYPE.FAVORITES, // Cart + Favorites has same pageType
    PAGE_TYPE.HOME,
    PAGE_TYPE.INVOICE,
    PAGE_TYPE.PDP,
    PAGE_TYPE.SEARCH,
    PAGE_TYPE.SOURCING
];

let loadPromise = null;

function loadIntercom() {
    loadPromise = ScriptInjector
        .inject(`https://widget.intercom.io/widget/${intercomId}`)
        .then(() => {
            const intercom = root.Intercom;
            intercom('boot', {
                'app_id': intercomId
            });

            return intercom;
        });
    return loadPromise;
};

function getIntercom() {
    if (root.intercom || loadPromise) {
        return loadPromise;
    }
    return loadIntercom();
}

function getDate() {
    const date = new Date();
    return date.toJSON();
}

function getIntercomDate(date) {
    date = new Date(date);
    return date.getTime() / 1000; // get UTC seconds
}

function applyArgumentQueue(intercom) {
    const argumentQueue = sessionStorage.getItem(ARGUMENTS_STORAGE_KEY) || [];

    if (argumentQueue.length) {
        argumentQueue.forEach(args => {
            intercom.apply(intercom, args);
        });
    }

    sessionStorage.setItem(ARGUMENTS_STORAGE_KEY, []);
}

function load(...args) {
    const chatStarted = sessionStorage.getItem(CHAT_STARTED_STORAGE_KEY);
    const pageTypeValid = isPageType(PAGE_TYPE_WHITELIST);

    if (chatStarted && !isPrintPDP && pageTypeValid) {
        return getIntercom().then(intercom => {
            applyArgumentQueue(intercom);
            if (args.length) {
                intercom.apply(intercom, args);
            }
        });
    } else if (!isPrintPDP) {
        const argumentQueue = sessionStorage.getItem(ARGUMENTS_STORAGE_KEY) || [];
        argumentQueue.push(args);
        sessionStorage.setItem(ARGUMENTS_STORAGE_KEY, argumentQueue);
    }

    // if `chat_intercom` is not enabled resolve
    return Promise.resolve();
}

function trackEvent(...args) {
    return load('trackEvent', ...args);
}

function update(data) {
    return load('update', data);
}

function getUserTestGroup(tradeUserCreateDate) {
    const userId = getUserId(document.cookie);
    const lastIdNumber = Number.parseInt(userId.slice(-1));
    const testGroupData = {};

    // User ids end up with 1 or 2, ~20% of users
    if ([1, 2].indexOf(lastIdNumber) > -1) {
        testGroupData.user_test_group = 'Holdout'; // eslint-disable-line camelcase
    }

    testGroupData.user_test_group = 'Test'; // eslint-disable-line camelcase

    if (tradeUserCreateDate) {
        const secontToLastIdNumber = Number.parseInt(userId.slice(-2, -1));
        if ([4, 5].indexOf(secontToLastIdNumber) > -1 && new Date(tradeUserCreateDate) > new Date(TRADE_PROMO_DATE_FROM)) {
            testGroupData.user_trade_promo_group = 'Test'; // eslint-disable-line camelcase
        } else {
            testGroupData.user_trade_promo_group = 'Control'; // eslint-disable-line camelcase
        }
    }

    return testGroupData;
};

function getCustomerType(user) {
    const customerType = [];

    if (user && user.isTrade) {
        customerType.push(CUSTOMER_TYPE_TRADE);
    }

    if (user && user.hasVipPermission) {
        customerType.push(CUSTOMER_TYPE_VIP);
    }

    if (!customerType.length) {
        return CUSTOMER_TYPE_CONSUMER;
    }

    return customerType.length ? customerType.join(' and ') : undefined;
};

const chat = {
    start() {
        sessionStorage.setItem(CHAT_STARTED_STORAGE_KEY, true);
        return load();
    },

    show() {
        return load('show');
    },

    fillUserData(viewer, user) {
        const { tradeChatEmail, vipChatEmail } = viewer;
        const { tradeUserCreateDate } = user;
        if (!getUserId(root.document.cookie)) {
            return Promise.resolve();
        }

        const userType = getCustomerType(user);
        const userId = getUserId(document.cookie);
        const {createdDate, profile: {firstName, lastName, email}} = user;

        const userData = Object.assign({
            'user_id': userId,
            'name': `${firstName || ''} ${lastName || ''}`.trim(),
            'email': email,
            'user_type': userType,
            'registered_at': getIntercomDate(createdDate),
            'trade_account_manager': get(tradeChatEmail, '0.userDataB.0.email'),
            'vip_account_manager': get(vipChatEmail, '0.userDataB.0.email')
        }, getUserTestGroup(tradeUserCreateDate));

        return update(userData);
    },

    customAttribute: {
        transactionsStatus({buyerStatus, totalTransactions}) {
            return update({
                'buyer_status': buyerStatus,
                'total_transactions': totalTransactions
            });
        },

        tradeStatus(status) {
            return update({'trade_status': status});
        },

        verifiedTradeDate(date) {
            return update({'verified_trade_at': getIntercomDate(date)});
        },

        itemsInCart(itemCount) {
            return update({'items_in_cart': itemCount});
        },

        registeredOnPage() {
            const {pathname} = root.location;
            return update({'registered_on_page': pathname});
        },

        loyaltyStatus(status) {
            return update({'LoyaltyStatus': status});
        }
    },

    track: {
        abandonFavorite(type) {
            switch (type) {
                case TYPE_SEARCH:
                    return trackEvent('abandon_follow_search');

                case TYPE_DEALER:
                    return trackEvent('abandon_follow_dealer');
            }

            return Promise.resolve();
        },

        abandonRequestNetPrice() {
            return trackEvent('abandon_request_net_price', {
                'fired_at': getDate()
            });
        },

        requestNetPrice() {
            return trackEvent('success_request_net_price', {
                'fired_at': getDate()
            });
        },

        abandonMakeOffer() {
            return trackEvent('make_offer_abandon', {
                'fired_at': getDate()
            });
        },

        makeOfferComplete() {
            return trackEvent('make_offer_complete', {
                'fired_at': getDate()
            });
        },

        abandonContactDealer() {
            return trackEvent('abandon_contact_dealer', {
                'fired_at': getDate()
            });
        },

        contactDealer() {
            return trackEvent('contact_dealer', {
                'fired_at': getDate()
            });
        },

        addToCart() {
            return trackEvent('item_added_to_cart', {
                'fired_at': getDate()
            });
        },

        itemSaved() {
            return trackEvent('success_save_item');
        },

        shippingDetailsClick() {
            return trackEvent('view_shipping_details');
        },

        buyerAssuranceClick() {
            return trackEvent('buyer_protection');
        },

        checkoutReached(data, isNegotiable) {
            sessionStorage.setItem(CHECKOUT_TYPE_KEY, {isNegotiable});
            return trackEvent('checkout_reached', data);
        },

        checkoutComplete() {
            return trackEvent('checkout_completed');
        },

        checkoutAbandon() {
            return trackEvent('checkout_abandon');
        },

        checkoutSurveySubmitted(data) {
            return trackEvent('checkout_survey_submitted', data);
        },

        tradeBasicInfoCompleted(noWebsite) {
            const prefix = noWebsite ? 'no ' : '';
            return trackEvent(`trade application step 1 - ${prefix}website`);
        },

        sourcingRequestSubmitted() {
            return trackEvent('SourcingRequest');
        }
    }

};

function checkAbandonCheckout() {
    if (!root.location) {
        return;
    }
    const {pathname} = root.location;
    const isCheckoutPage = pathname.indexOf('checkout') > -1;
    const isCheckoutConfirmationPage = pathname.indexOf('checkout/confirmation') > -1;
    const isMyOrdersPage = pathname.indexOf('my/orders') > -1;

    if (isCheckoutPage && !isCheckoutConfirmationPage) {
        root.onbeforeunload = function () {
            sessionStorage.setItem(CHECKOUT_UNLOAD_KEY, true);
        };
    } else if (!isCheckoutConfirmationPage && !isMyOrdersPage && sessionStorage.getItem(CHECKOUT_UNLOAD_KEY)) {
        const isNegotiable = get(sessionStorage.getItem(CHECKOUT_TYPE_KEY), 'isNegotiable');

        if (isNegotiable) {
            chat.track.abandonMakeOffer();
        } else {
            chat.track.checkoutAbandon();
        }
    }

    sessionStorage.deleteItem(CHECKOUT_UNLOAD_KEY);
};

function checkConductSearch() {
    if (!root.location) {
        return;
    }
    const {pathname, search} = root.location;

    if (pathname.indexOf('/search') === 0 && search.indexOf('?q=') === 0) {
        update({'user_conduct_search': 'Yes'});
    }
};


function checkItemsAbovePrice(key, eventName) {
    const items = localStorage.getItem(ITEMS_ABOVE_PRICE_VIEWED_KEY) || {};
    let newValue = (items[key] || 0) + 1;

    if (newValue >= ITEMS_ABOVE_PRICE_VIEWED_THRESHOLD) {
        trackEvent(eventName);
        newValue = 0;
    }
    localStorage.setItem(ITEMS_ABOVE_PRICE_VIEWED_KEY, {
        ...items,
        [key]: newValue
    });
};

function checkPdp() {
    if (isPageType([PAGE_TYPE.PDP])) {
        const pk = SV.get('item.pk');
        trackEvent('pdp_view', {pk});

        const isUserTypeTrade = SV.get('settings.isUserTypeTrade');

        if (isUserTypeTrade) {
            const { netPrice, price } = SV.get('data') || {};
            const tradePrice = netPrice || price;
            if (tradePrice > PRICE_LIMIT_50K) {
                checkItemsAbovePrice(PRICE_LIMIT_50K, 'ItemView50k');
            } else if (tradePrice > PRICE_LIMIT_10K) {
                checkItemsAbovePrice(PRICE_LIMIT_10K, 'ItemView10k');
            }
        }
    }
};

checkAbandonCheckout();
checkConductSearch();
checkPdp();

export const userFragment = graphql`
    fragment chat_user on User {
        createdDate
        isTrade: hasPermission(name: "B_TRADE_CHECKOUT")
        hasVipPermission: hasPermission(name: "B_VIP_CHECKOUT")
        profile {
            email
        }
    }
`;

export const viewerFrgament = graphql`
    fragment chat_viewer on Viewer {
        tradeChatEmail:userRelationships(userIds: $userIds, relationshipType: VIP){
            userDataB {
                ... on VipRepData {
                    email
                }
            }
        }
        vipChatEmail:userRelationships(userIds: $userIds, relationshipType: TRADE){
            userDataB {
                ... on TradeRepData {
                    email
                }
            }
        }
    }
`;

export default chat;



// WEBPACK FOOTER //
// ./src/utils/chat.js